import { ROUTES } from '@/app/routes';
import type { DateRange } from '@/components/ui/FilterBar';

/**
 * What the dashboard shows, and the words it shows it with.
 *
 * The page renders from these lists rather than naming metrics itself, so
 * adding a row when the client approves petitions, or renaming one, is an
 * edit here and nowhere else. Where each number comes from in the analytics
 * reply is `toDashboardData.ts`.
 */

/**
 * Said in place of the number of anything whose feature is not built yet, and
 * on Download PDF: one wording for one meaning (P9).
 */
export const LATER_RELEASE = 'Available in a later release';

/** The four cards of the October wireframe (backend intent D17). */
export type CardId = 'totalSupporters' | 'arcViews' | 'contentViews' | 'advocacyActions';

/** The rows of the two tables. */
export type RowId =
  | 'campaignViews'
  | 'callClicks'
  | 'emailClicks'
  | 'guideViews'
  | 'surveyViews'
  | 'surveySubmissions'
  | 'pageViews'
  | 'documentViews'
  | 'documentDownloads'
  | 'volunteerClicks'
  | 'volunteerSubmissions'
  | 'fundraisingClicks'
  | 'newSupporters'
  | 'newMembers'
  | 'totalMembers'
  | 'invitesAccepted'
  | 'notificationOpens'
  | 'notificationClicks'
  | 'orgContentViews';

export type TrendTab = 'overview' | 'advocacy' | 'content' | 'notifications';

export type MetricTableId = 'advocacy' | 'supporters';

export type ActivityId =
  | 'activeCampaigns'
  | 'currentGuides'
  | 'openSurveys'
  | 'scheduledNotifications'
  | 'pendingInvitations';

export interface DailyPoint {
  /** `YYYY-MM-DD`. */
  date: string;
  value: number;
}

/**
 * Everything the page needs for one range, shaped for the page: the reply
 * from the backend is mapped into this once, in `toDashboardData`.
 */
export interface DashboardData {
  /** The window the numbers cover, as the backend resolved it. */
  range: DateRange;
  /** The window of equal length before it, which the cards compare against. */
  previous: DateRange;
  /** Each card's number for the range and for the period before it. */
  cards: Record<CardId, { current: number; previous: number }>;
  /** Each table row's count. Total members is the count now; the rest are within the range. */
  rows: Record<RowId, number>;
  /**
   * The previous period's count, for the rows that show a change: New
   * supporters, New members and Total members (P5). No other row has one.
   */
  previousRows: Record<RowWithChange, number>;
  /** One point per day in the range, for each tab. */
  series: Record<TrendTab, DailyPoint[]>;
  /** What is live right now, whatever the range. */
  activity: Record<ActivityId, number>;
}

export interface CardDefinition {
  id: CardId;
  label: string;
  /** The small line underneath, saying what is counted. */
  caption: string;
}

export interface RowDefinition {
  id: RowId;
  /** The row's name in its table. For a paired row, it names both numbers. */
  label: string;
  /** The table it is a row of. The second metric of a pair has none. */
  table: MetricTableId | null;
  /** The metric shown after the slash in the same row. */
  pairedWith?: RowId;
  /**
   * Whether the feature that records it is built. When it is not, the table
   * says so in place of the number (P7): the reply carries every registered
   * type at 0, so it cannot tell a real 0 from a feature not built yet.
   */
  available: boolean;
}

/**
 * Req §19: a click is not a completed action. Campaign metrics are named as
 * clicks and views, fundraising as clicks, never as donations. The one card
 * the wireframe names as actions says in its caption that it counts clicks
 * and submissions inside the platform.
 */
export const CARDS: readonly CardDefinition[] = [
  {
    id: 'totalSupporters',
    label: 'Total Supporters',
    // Followers plus invited members, as the Supporters page counts (P4).
    caption: 'Followed and invited supporters',
  },
  { id: 'arcViews', label: 'ARC Views', caption: 'Opens of your resource center' },
  {
    id: 'contentViews',
    label: 'Content Views',
    caption: 'Pages, documents and downloads',
  },
  {
    id: 'advocacyActions',
    label: 'Advocacy Actions',
    caption: 'Clicks and submissions inside ActivistARC',
  },
];

/** Every row, in the order each table shows them. */
export const ROWS: readonly RowDefinition[] = [
  {
    id: 'campaignViews',
    label: 'Campaign page views',
    table: 'advocacy',
    available: false,
  },
  { id: 'callClicks', label: 'Call button clicks', table: 'advocacy', available: false },
  {
    id: 'emailClicks',
    label: 'Email button clicks',
    table: 'advocacy',
    available: false,
  },
  { id: 'guideViews', label: 'Guide views', table: 'advocacy', available: false },
  {
    id: 'surveyViews',
    label: 'Survey views / submissions',
    table: 'advocacy',
    pairedWith: 'surveySubmissions',
    available: false,
  },
  { id: 'surveySubmissions', label: 'Survey submissions', table: null, available: false },
  { id: 'pageViews', label: 'Page views', table: 'advocacy', available: true },
  {
    id: 'documentViews',
    label: 'Document views / downloads',
    table: 'advocacy',
    pairedWith: 'documentDownloads',
    available: true,
  },
  { id: 'documentDownloads', label: 'Document downloads', table: null, available: true },
  {
    id: 'volunteerClicks',
    label: 'Volunteer clicks / submissions',
    table: 'advocacy',
    pairedWith: 'volunteerSubmissions',
    available: true,
  },
  // The clicks are real; native volunteer forms arrive later (P8).
  {
    id: 'volunteerSubmissions',
    label: 'Volunteer submissions',
    table: null,
    available: false,
  },
  {
    id: 'fundraisingClicks',
    label: 'Fundraising button clicks',
    table: 'advocacy',
    available: true,
  },
  { id: 'newSupporters', label: 'New supporters', table: 'supporters', available: true },
  { id: 'newMembers', label: 'New members', table: 'supporters', available: true },
  { id: 'totalMembers', label: 'Total members', table: 'supporters', available: true },
  {
    id: 'invitesAccepted',
    label: 'Invites accepted',
    table: 'supporters',
    available: false,
  },
  {
    id: 'notificationOpens',
    label: 'Notification opens',
    table: 'supporters',
    available: false,
  },
  {
    id: 'notificationClicks',
    label: 'Notification clicks',
    table: 'supporters',
    available: false,
  },
  {
    id: 'orgContentViews',
    label: 'Organization content views',
    table: 'supporters',
    available: false,
  },
];

/**
 * The rows that show their change against the previous period (P5): the rows
 * `previousRows` carries, each mapped to its previous in `toDashboardData`.
 * Known before the reply, so a row can hold the change line's place while
 * loading (P35).
 */
export const ROWS_WITH_CHANGE = [
  'newSupporters',
  'newMembers',
  'totalMembers',
] as const satisfies readonly RowId[];

export type RowWithChange = (typeof ROWS_WITH_CHANGE)[number];

/** Whether a row shows its change against the previous period. */
export const showsChange = (id: RowId): id is RowWithChange =>
  (ROWS_WITH_CHANGE as readonly RowId[]).includes(id);

/** The two tables, with the wireframe's captions. */
export const METRIC_TABLES = [
  { id: 'advocacy', caption: 'Advocacy & Resources: performance by feature' },
  { id: 'supporters', caption: 'Supporters & Communications: audience growth and reach' },
] as const satisfies readonly { id: MetricTableId; caption: string }[];

/** A table's rows, in order. */
export function tableRows(table: MetricTableId): RowDefinition[] {
  return ROWS.filter((row) => row.table === table);
}

/** The tabs, in order. Each is a daily total the backend sends as its own key. */
export const TREND_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'advocacy', label: 'Advocacy' },
  { id: 'content', label: 'Content' },
  { id: 'notifications', label: 'Notifications' },
] as const satisfies readonly { id: TrendTab; label: string }[];

/** One Current Activity entry. */
export interface ActivityEntry {
  id: ActivityId;
  label: string;
  /** Where that work is managed, once its section is built. */
  to: string;
  /**
   * Whether its section is built. Until it is, the entry says so in place of
   * its count and links nowhere: a link to a placeholder is a dead end (P7).
   */
  available: boolean;
}

/** The Current Activity entries. None of their sections is built yet. */
export const ACTIVITY = [
  {
    id: 'activeCampaigns',
    label: 'Active Campaigns',
    to: ROUTES.campaigns.active,
    available: false,
  },
  {
    id: 'currentGuides',
    label: 'Current Guides',
    to: ROUTES.guides.current,
    available: false,
  },
  { id: 'openSurveys', label: 'Open Surveys', to: ROUTES.surveys.all, available: false },
  {
    id: 'scheduledNotifications',
    label: 'Scheduled Notifications',
    to: ROUTES.notifications.scheduled,
    available: false,
  },
  // Counts staff invitations today; shown once member invitations exist (C4).
  {
    id: 'pendingInvitations',
    label: 'Pending Invitations',
    to: ROUTES.supporters.invitations,
    available: false,
  },
] as const satisfies readonly ActivityEntry[];
